import moment from 'moment';
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CircleSolidInfoIcon, PortfolioBalanceIcon } from '../../assets/icons';
import { Button } from '../../components';
import { ViewStateType } from '../../components/ProductTemplates/types';
import { AppContext } from '../../context/ContextProvider';
import { AppEventTypes } from '../../types/AppType';
import { NavigationPath, PortfolioBalance } from '../../types/DomainTypes';
import { buildDisplayText, classNames, formatterGbp } from '../../utils';
import { usePortfolioBalances } from '../Portfolio/components/Summary/hooks/usePortfolioBalances';
import { FeedbackTemplate, MessageType } from './FeedbackTemplate';
import {
  AccountViewType,
  ModelKeys,
  ModelType,
  PaymentType,
  SubjectOptionKeys,
  TopupSlideoutViewState,
  TopupSlideoutViewType,
} from '../Accounts/types';
import BankTransferTemplate from '../Accounts/components/slideoutTemplates/BankTransferTemplate';
import OTPTopup from '../Accounts/components/slideoutTemplates/OTPTopup';
import RecurringTopup from '../Accounts/components/slideoutTemplates/RecurringTopup';
import StripeOTP from '../Accounts/components/Stripe/StripeOTP';
import StripeRecurringPayment from '../Accounts/components/Stripe/StripeRecurringPayment';
import { StripeRedirectKeys } from '../Accounts/components/Stripe/types';
import { CheckoutOtp } from '../Accounts/components/Checkout.Com/CheckoutOtp';
import MgmtPaymentTypeSelector, { RadioBtnOption } from './MgmtFeePolicyEnforcer/mgmtPaymentTypeSelector';
import { HowToPayEnum } from './HowToPay/type';

export interface TopupSlideoutRefType {
  onBack: () => void;
}

enum DisplayTextKeys {
  TOPUP_AMOUNT_TEXT = 'top_up_amount',
  PORTFOLIO_PLACEHOLDER_TEXT = 'portfolio_placeholder_text',
  PAYMENT_PLACEHOLDER_TEXT = 'payment_placeholder_text',
  SELECT_PORTFOLIO_TITLE = 'select_portfolio_title',
  PAYMENT_FREQUENCY_TITLE = 'payment_frequency_title',
  PAYMENT_METHOD_TITLE = 'payment_method_title',
  TOPUP_TEXT = 'account:text.topup',
  NEXT = 'next',
  RECURRING_PAYMENT_TEXT = 'recurring_payment_text',
  FREQUENCY_TEXT = 'frequency_text',
  START_DATE_TEXT = 'start_date_text',
  CONTACT_US = 'common:contact',
  RECURRING_PAYMENTS = 'recurring-payments',
  OTP_CREDIT_CARD = 'credit-card',
  BANK_TRANSFER = 'bank-transfer',
}

const dateFormat = 'DD MMM YYYY';

interface TopupSlideoutProps {
  onClose?: (nextView?: AccountViewType) => void;
  onStateChange?: (state: TopupSlideoutViewState) => void;
  initialModel?: ModelType;
  initialView?: TopupSlideoutViewType;
}
const defaultValue = {
  [ModelKeys.AMOUNT]: '',
  [ModelKeys.PORTFOLIO_ID]: '',
  [ModelKeys.PAYMENT_TYPE]: PaymentType.CREDIT_CARD,
  [ModelKeys.IS_RECURRING]: false,
  [ModelKeys.START_DATE]: moment().format(dateFormat),
  [ModelKeys.FREQUENCY_TYPE]: '',
};

export const TopupSlideout = forwardRef(
  (
    {
      onClose,
      onStateChange,
      initialModel = defaultValue,
      initialView = TopupSlideoutViewType.NONE,
    }: TopupSlideoutProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    const displayText = useMemo(
      () => buildDisplayText(Object.values(DisplayTextKeys), 'account:slideout.topup', t),
      [t],
    );
    const [feedbackState, setFeedbackState] = useState(ViewStateType.DEFAULT);

    const {
      state: {
        settings: { currency },
        app: { paymentType, isConfirmPayment, hasCard },
      },
      dispatch,
    } = useContext(AppContext);

    const [selectedOption, setSelectedOption] = useState<RadioBtnOption | null>();
    const [selectedTopupTypeId, setSelectedTopTypeId] = useState('');
    const [view, setView] = useState(initialView);

    const [model, setModel] = useState<ModelType>({ ...defaultValue, ...initialModel });
    const [feedbackTemplateConfig, setFeedbackTemplateConfig] = useState<MessageType>({
      onClick: () => null,
      content: () => null,
      footer: null,
    });

    // function closeModal() {
    //   onClose?.();
    //   setIsOpen(false);
    // }
    const onOptionSelected = (selected: RadioBtnOption) => {
      setSelectedOption(selected);
    };

    const options = useMemo((): RadioBtnOption[] => {
      const baseOptions: RadioBtnOption[] = [
        {
          id: HowToPayEnum.CARD_ON_FILE,
          label: 'Card on file',
          value: HowToPayEnum.CARD_ON_FILE,
          description: (
            <span className="text-xs flex space-x-2 text-gray-600 dark:text-gray-400">
              <span>Select from an existing card saved on your account.</span>
            </span>
          ),
          data: '',
        },
        {
          id: HowToPayEnum.NEW_CARD,
          label: 'New Card',
          value: HowToPayEnum.NEW_CARD,
          description: (
            <span className="text-xs flex space-x-2 text-gray-600 dark:text-gray-400">
              <span>Pay with a new card.</span>
            </span>
          ),
          data: '',
        },
      ];

      return hasCard ? baseOptions : [baseOptions[1]];
    }, [hasCard]);

    const onTopupTypeSelect = () => {
      let nView = TopupSlideoutViewType.NONE;
      let showBackButton = false;
      switch (selectedTopupTypeId) {
        case TopupSlideoutViewType.BANK_TRANSFER:
          nView = TopupSlideoutViewType.BANK_TRANSFER;
          showBackButton = true;
          break;
        case TopupSlideoutViewType.RECURRING_PAYMENTS:
        case TopupSlideoutViewType.OTP_CREDIT_CARD:
          nView = selectedTopupTypeId as TopupSlideoutViewType;
          showBackButton = true;
          break;
        default:
          break;
      }
      setView(nView);
      if (onStateChange)
        onStateChange({
          showBackButton,
          view: nView,
        });
    };
    const onViewStateChange = (change: TopupSlideoutViewState) => {
      setModel({ ...model, ...(change.model || {}) });
      setView(change.view);
      if (onStateChange) onStateChange(change);
    };

    const onCTA = (requestView: ViewStateType) => {
      switch (requestView) {
        case ViewStateType.ERROR_RESULT:
          setView(TopupSlideoutViewType.ERROR);
          setFeedbackTemplateConfig({
            footer: (
              <div className="flex flex-col w-full">
                <Button
                  className="btn-outline mt-5"
                  onClick={() => {
                    onCTA(ViewStateType.CONTACT_US);
                  }}
                  props={{
                    name: displayText[DisplayTextKeys.CONTACT_US],
                  }}
                >
                  {displayText[DisplayTextKeys.CONTACT_US]}
                </Button>
              </div>
            ),
          });
          setFeedbackState(ViewStateType.ERROR_RESULT);
          if (onStateChange)
            onStateChange({
              showBackButton: false,
              view: TopupSlideoutViewType.ERROR,
            });
          break;

        case ViewStateType.PAYMENT_SUCCESS:
          setView(TopupSlideoutViewType.NONE);
          setFeedbackTemplateConfig({
            onClick: () => {
              if (onClose) onClose();
            },

            subTitle: (
              <div className="text-14 mt-2">
                {t`paymentSuccess.subTitle${
                  paymentType === TopupSlideoutViewType.STRIPE_RECURRING_PAYMENT ? '_recurring' : ''
                }`.trim()}
              </div>
            ),
          });
          setFeedbackState(requestView);
          dispatch({ type: AppEventTypes.UPDATE_STATE, payload: { isConfirmPayment: false } });
          break;

        case ViewStateType.CONTACT_US:
          if (onClose) onClose(AccountViewType.CONTACT_US);
          navigate(NavigationPath.ACCOUNTS, {
            state: { accountViewType: AccountViewType.CONTACT_US, subject: SubjectOptionKeys.FINANCIAL },
          });
          break;

        default:
          setView(TopupSlideoutViewType.NONE);
          setFeedbackState(requestView);
          break;
      }
    };

    const onBack = () => {
      let showBackButton = true;
      const viewUpdate = TopupSlideoutViewType.NONE;
      switch (view) {
        case TopupSlideoutViewType.BANK_TRANSFER:
          showBackButton = false;
          break;
        case TopupSlideoutViewType.OTP_CREDIT_CARD:
          showBackButton = false;
          break;

        default:
          showBackButton = false;
          break;
      }

      setView(viewUpdate);
      if (onStateChange)
        onStateChange({
          showBackButton,
          view: viewUpdate,
        });
    };

    const { portfolioBalances } = usePortfolioBalances();

    const feeModelInfo = useMemo(() => {
      const feeModels = portfolioBalances.filter((x: PortfolioBalance) => `${x.portfolioId}`.length > 0);
      const otherFeeModelExist = !!feeModels.find((x) => x.currentFeeModel === false);

      const canTopUp = feeModels.filter(
        (x: PortfolioBalance) => `${x.portfolioId}`.length > 0 && !!x.currentFeeModel && x.currentFeeModel === true,
      );
      return {
        showFeeModelInfo: otherFeeModelExist,
        canTopUp: canTopUp && canTopUp.length > 0,
      };
    }, [portfolioBalances]);

    useImperativeHandle(ref, () => ({
      onBack,
    }));

    const onConfirmPayment = (queryParams: URLSearchParams) => {
      let paymentInfo: Record<string, string> = {};
      for (const [key, value] of queryParams) {
        paymentInfo = { ...paymentInfo, [key]: value };
      }

      if (
        paymentInfo[StripeRedirectKeys.REDIRECT_STATUS].length > 0 &&
        paymentInfo[StripeRedirectKeys.CLIENT_SECRET].length > 0 &&
        paymentInfo[StripeRedirectKeys.REDIRECT_STATUS] === 'succeeded'
      ) {
        onCTA(ViewStateType.PAYMENT_SUCCESS);
        dispatch({
          type: AppEventTypes.UPDATE_STATE,
          payload: {
            isConfirmPayment: false,
          },
        });
      }
      const rootUrl = window.location.pathname.replace(NavigationPath.PAYMENT_CONFIRMATION, '');
      window.history.replaceState(null, '', rootUrl);
    };

    useEffect(() => {
      if (isConfirmPayment && pathname.endsWith(NavigationPath.PAYMENT_CONFIRMATION)) {
        onConfirmPayment(new URLSearchParams(search));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConfirmPayment]);

    return (
      <div className="flex flex-col flex-1 bg-gray-50 dark:bg-slate-900 pb-5 px-4 w-full relative overflow-x-hidden sm:w-[390px]">
        <div className="w-full h-full overflow-y-auto flex flex-col">
          <FeedbackTemplate
            onClose={onClose}
            templateConfig={feedbackTemplateConfig}
            viewState={feedbackState}
            onCTA={onCTA}
          >
            <div className="flex flex-col h-full w-full">
              {view === TopupSlideoutViewType.NONE && (
                <div className="w-full h-full p-4 flex flex-col">
                  {/* Header */}
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {t('account:slideout.topup.select_method_title', 'Select Payment Method')}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t(
                        'account:slideout.topup.select_method_subtitle',
                        'Choose how you would like to top up your account',
                      )}
                    </p>
                  </div>

                  {/* Payment Method Cards */}
                  <div className="flex flex-col gap-3 flex-1">
                    {[
                      {
                        type: TopupSlideoutViewType.OTP_CREDIT_CARD,
                        icon: '💳',
                        description: t('account:slideout.topup.otp_description', 'One-time payment using your card'),
                      },
                      {
                        type: TopupSlideoutViewType.RECURRING_PAYMENTS,
                        icon: '🔄',
                        description: t(
                          'account:slideout.topup.recurring_description',
                          'Set up automatic recurring payments',
                        ),
                      },
                      {
                        type: TopupSlideoutViewType.BANK_TRANSFER,
                        icon: '🏦',
                        description: t('account:slideout.topup.bank_description', 'Transfer directly from your bank'),
                      },
                    ].map(({ type, icon, description }) => {
                      const isSelected = selectedTopupTypeId === type;
                      return (
                        <div
                          onClick={() => setSelectedTopTypeId(type)}
                          key={`topupType-${type}`}
                          className={classNames(
                            'cursor-pointer flex items-center p-4 w-full rounded-xl border-2 transition-all duration-200',
                            isSelected
                              ? 'border-orange dark:border-teal-400 bg-orange/5 dark:bg-teal-400/10 shadow-md'
                              : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-sm',
                          )}
                        >
                          <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-2xl mr-4">
                            {icon}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-white">{displayText[type]}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
                          </div>
                          <div
                            className={classNames(
                              'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                              isSelected
                                ? 'border-orange dark:border-teal-400 bg-orange dark:bg-teal-400'
                                : 'border-gray-300 dark:border-slate-600',
                            )}
                          >
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Info Section */}
                  {feeModelInfo.showFeeModelInfo && (
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700">
                      <div className="flex items-start gap-3">
                        <CircleSolidInfoIcon className="text-blue-500 dark:text-teal-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-sm">
                            {t`account:slideout.topup.recurring_payment_info_title`}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {t(`account:slideout.topup.current_fee_model_info`)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="mt-6">
                    {feeModelInfo.canTopUp ? (
                      <Button
                        isDisable={selectedTopupTypeId === ''}
                        className={classNames(
                          'w-full py-3 rounded-xl font-medium transition-all duration-200',
                          selectedTopupTypeId === ''
                            ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                            : 'bg-orange dark:bg-teal-500 text-white hover:bg-orange/90 dark:hover:bg-teal-600 shadow-lg shadow-orange/25 dark:shadow-teal-500/25',
                        )}
                        onClick={onTopupTypeSelect}
                        props={{
                          name: DisplayTextKeys.NEXT,
                        }}
                      >
                        {displayText[DisplayTextKeys.NEXT]}
                      </Button>
                    ) : (
                      <Button
                        className="w-full py-3 rounded-xl font-medium bg-orange dark:bg-teal-500 text-white hover:bg-orange/90 dark:hover:bg-teal-600 shadow-lg shadow-orange/25 dark:shadow-teal-500/25 transition-all duration-200"
                        onClick={() => onCTA(ViewStateType.CONTACT_US)}
                        props={{
                          name: DisplayTextKeys.CONTACT_US,
                        }}
                      >
                        {displayText[DisplayTextKeys.CONTACT_US]}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {view === TopupSlideoutViewType.RECURRING_PAYMENTS && (
                <RecurringTopup initialModel={model} onStateChange={onViewStateChange} />
              )}
              {view === TopupSlideoutViewType.OTP_CREDIT_CARD && (
                <OTPTopup initialModel={model} onStateChange={onViewStateChange} />
              )}
              {view === TopupSlideoutViewType.BANK_TRANSFER && <BankTransferTemplate />}
              {view === TopupSlideoutViewType.STRIPE_RECURRING_PAYMENT && (
                <StripeRecurringPayment
                  model={model}
                  onCTA={onCTA}
                  request={{
                    currency,
                    priceId: '',
                    portfolioId: Number(model[ModelKeys.PORTFOLIO_ID]),
                    defaultPaymentMethod: null,
                    amount: Number(model[ModelKeys.AMOUNT]),
                    frequency: model[ModelKeys.FREQUENCY_TYPE],
                  }}
                />
              )}
              {view === TopupSlideoutViewType.STRIPE_OTP_PAYMENT && (
                <StripeOTP
                  model={model}
                  onCTA={onCTA}
                  request={{
                    amount: Number(model[ModelKeys.AMOUNT]),
                    portfolioId: Number(model[ModelKeys.PORTFOLIO_ID]),
                  }}
                />
              )}
              {view === TopupSlideoutViewType.CHECKOUT_OTP_PAYMENT && (
                <div className="flex flex-col h-full">
                  {/* Amount Display */}
                  <div className="flex items-center flex-col py-8 px-5 bg-gradient-to-b from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 border-b border-gray-100 dark:border-slate-700">
                    <div className="w-14 h-14 rounded-full bg-orange/10 dark:bg-teal-500/10 flex items-center justify-center mb-3">
                      <PortfolioBalanceIcon className="w-7 h-7 text-orange dark:text-teal-400" />
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {t('account:slideout.topup.top_up_amount')}
                    </span>
                    <span className="text-3xl font-semibold text-gray-900 dark:text-white">
                      {formatterGbp.format(Number(model[ModelKeys.AMOUNT]))}
                    </span>
                  </div>

                  {/* Payment Type Selector */}
                  <MgmtPaymentTypeSelector
                    onSelected={onOptionSelected}
                    options={options}
                    selectedId={selectedOption?.id}
                  />

                  {/* Checkout Form */}
                  <div className="flex-1 bg-white dark:bg-slate-800">
                    <CheckoutOtp
                      showTopUpIcon={false}
                      isUseExistingCard={selectedOption?.id === (HowToPayEnum.CARD_ON_FILE as string)}
                      amount={Number(model[ModelKeys.AMOUNT])}
                      portfolioId={Number(model[ModelKeys.PORTFOLIO_ID])}
                    />
                  </div>
                </div>
              )}
            </div>
          </FeedbackTemplate>
        </div>
      </div>
    );
  },
);
